import { ArrowLeftIcon, LockClosedIcon } from '@heroicons/react/24/outline'
import { Link, useParams } from 'react-router-dom'

import { BASE } from './_Layout'
import { MOCK_FORMER_PATIENT_DETAILS } from './mockData'

function formatDate(value?: string | null) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function ProviderFormerPatient() {
    const { id } = useParams()
    const data = MOCK_FORMER_PATIENT_DETAILS[id ?? ''] ?? null

    if (!data) {
        return (
            <div className='max-w-5xl mx-auto px-4 py-12 text-center text-sm text-neutral-500'>
                Could not load this former client.{' '}
                <Link to={`${BASE}/clients`} className='text-brand-600 underline'>
                    Back to Clients
                </Link>
            </div>
        )
    }

    const { family, notes, homework } = data

    return (
        <div className='max-w-5xl mx-auto px-4 sm:px-6 py-8'>
            <Link
                to={`${BASE}/clients`}
                className='inline-flex items-center gap-1.5 text-sm text-brand-600 hover:underline mb-4'
            >
                <ArrowLeftIcon className='w-4 h-4' />
                Clients
            </Link>

            {/* Header */}
            <div className='bg-white border border-neutral-200 rounded-2xl p-6 mb-6'>
                <div className='flex items-center gap-2'>
                    <h1 className='text-xl font-bold text-neutral-900'>{family.childName ?? family.email}</h1>
                    <span className='text-xs font-medium text-neutral-600 bg-neutral-100 border border-neutral-200 rounded-full px-2.5 py-1 capitalize'>
                        {family.status}
                    </span>
                </div>
                <p className='text-sm text-neutral-500 mt-1'>
                    {family.parentName ? `${family.parentName} · ` : ''}
                    {family.email}
                </p>
                <p className='text-xs text-neutral-500 mt-2'>
                    Invited {formatDate(family.invitedAt) ?? '—'}
                    {family.activatedAt && ` · Activated ${formatDate(family.activatedAt)}`}
                    {family.endedAt && ` · Ended ${formatDate(family.endedAt)}`}
                </p>
            </div>

            <div className='mb-6 p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700 flex items-start gap-2'>
                <LockClosedIcon className='w-4 h-4 mt-0.5 flex-shrink-0 text-neutral-500' />
                <span>
                    This sponsorship has ended, so the client&apos;s app activity is no longer visible. Your
                    team&apos;s notes and homework records below are preserved for your files.
                </span>
            </div>

            <div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
                {/* Notes */}
                <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-4'>Notes ({notes.length})</h2>
                    {notes.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No notes were recorded.</p>
                    ) : (
                        <ul className='space-y-4'>
                            {notes.map((n) => (
                                <li key={n._id} className='pb-4 border-b border-neutral-100 last:border-0 last:pb-0'>
                                    <p className='text-sm text-neutral-900 whitespace-pre-wrap'>{n.body}</p>
                                    <p className='text-xs text-neutral-500 mt-1'>
                                        {n.authorName ?? 'A teammate'} · {formatDate(n.createdAt)}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>

                {/* Homework */}
                <div className='bg-white border border-neutral-200 rounded-2xl p-6'>
                    <h2 className='text-lg font-semibold text-neutral-900 mb-4'>Homework ({homework.length})</h2>
                    {homework.length === 0 ? (
                        <p className='text-sm text-neutral-500'>No homework was assigned.</p>
                    ) : (
                        <ul className='space-y-4'>
                            {homework.map((h) => (
                                <li key={h._id} className='pb-4 border-b border-neutral-100 last:border-0 last:pb-0'>
                                    <div className='flex items-start justify-between gap-2'>
                                        <p className='text-sm font-medium text-neutral-900'>{h.title}</p>
                                        <span className='text-xs font-medium text-neutral-600 bg-neutral-100 rounded-full px-2 py-0.5 capitalize whitespace-nowrap'>
                                            {h.status}
                                        </span>
                                    </div>
                                    {h.description && <p className='text-xs text-neutral-600 mt-1'>{h.description}</p>}
                                    {h.completionNote && (
                                        <p className='text-xs text-neutral-600 mt-1 italic'>
                                            &ldquo;{h.completionNote}&rdquo;
                                        </p>
                                    )}
                                    <p className='text-xs text-neutral-500 mt-1'>
                                        Assigned {formatDate(h.createdAt)}
                                        {h.assignedByName && ` by ${h.assignedByName}`}
                                        {h.dueDate && ` · Due ${formatDate(h.dueDate)}`}
                                        {h.completedAt && ` · Completed ${formatDate(h.completedAt)}`}
                                    </p>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    )
}
